import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Inject,
  Input,
  OnInit,
  PLATFORM_ID,
  input,
  ViewEncapsulation
} from '@angular/core';
import { IconLookup, IconProp } from '@fortawesome/fontawesome-svg-core';
import { faBluesky, faFacebook, faInstagram, faWhatsapp, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope, faShareAlt } from '@fortawesome/free-solid-svg-icons';

import { ENVIRONMENT, EnvironmentService, TellFriendsParams, TellFriendsPlatform } from '@environment';
import { MovieLoadingComponent } from '@usheru-hff/ui/movie-detail-page';
import { TRACKING_SERVICE, TrackingService } from '@usheru-hff/sdk/tracking';
import { getMovieGenres, getMovieTags, Movie } from '@usheru-hff/shared/domain';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

interface SocialLink {
  icon: IconLookup;
  iconProp: IconProp;
  platform: TellFriendsPlatform;
  to: string;
}

/**
 * Component that shows links to share a movie in different social platforms.
 *
 * //TODO barbeitoalex propostal - refractor name to something like "movie-social-links".
 * //TODO barbeitoalex propostal - organice life cycle methods. Split in specific method to improve docs.
 * //TODO barbeitoalex propostal - update translate text with an observable to the current language
 * //TODO barbeitoalex propostal - subscribe to current language to update the translate pipes
 */
@Component({
  selector: 'ush-movie-tell-friends',
  templateUrl: './movie-tell-friends.component.html',
  styleUrls: ['./movie-tell-friends.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FontAwesomeModule, CommonModule],
  standalone: true
})
export class MovieTellFriendsComponent implements MovieLoadingComponent, OnInit {
  private readonly env = inject(ENVIRONMENT);
  /**
   * @docsNotRequired
   */
  readonly movie = input<Movie | null>(null);
  readonly isLoadingData = input(false);

  @Input()
  dropdownDesing: boolean;

  @Input()
  compacted: boolean;

  /**
   * Content of the shared message.
   */
  @Input()
  message: string;

  /**
   * Subject when shared with mail.
   */
  @Input()
  mailSubject: string;

  /**
   * Subject when shared with mail.
   */
  @Input()
  campaign: string;

  /**
   * @docsNotRequired
   */
  @Input()
  trackingCategory: string;

  /**
   * @deprecard Don't found any use
   */
  @Input()
  trackingExtraProps: unknown;

  /**
   * @docsNotRequired
   *
   * //TODO barbeitoalex propostal - remove the "to". Don't have any use.
   */
  socialLinks: SocialLink[] = [];

  shareIcon = faShareAlt;
  private readonly customInviteToMovieMsg = $localize`:@@movie.inviteToMovie:Want to see _movie_ with me?`;
  private readonly showBlueSky = this.env?.showBlueSky;
  get shareMessage(): string {
    const movie = this.movie();
    if (!movie) return this.message ?? '';
    return this.message ?? this.customInviteToMovieMsg.replace('_movie_', movie.title);
  }
  get shareMailSubject(): string {
    const movie = this.movie();
    return this.mailSubject ?? movie?.trTitle ?? movie?.title ?? '';
  }
  get shareCampaign(): string {
    return this.campaign ?? 'Sharing-%movieUrl%';
  }
  get resolvedTrackingCategory(): string {
    return this.trackingCategory ?? 'MovieDetail';
  }

  constructor(
    @Inject(TRACKING_SERVICE) private trackingService: TrackingService,
    private environmentService: EnvironmentService,
    @Inject(PLATFORM_ID) private platformId
  ) {}

  ngOnInit(): void {
    this.socialLinks = this.buildSocialLinks();
  }

  /**
   * @docsNotRequired
   * @param sl
   * @returns
   */
  onShareLink(sl: SocialLink): string {
    const movie = this.movie();
    if (!this.isBrowser || !movie) return '';

    const movieTitle = movie.title || '';
    const friendlyUrl = movie.friendlyUrl || '';

    const messageRaw = this.shareMessage.replace('_movie_', movieTitle);
    const subjectRaw = this.shareMailSubject.replace('_movie_', movieTitle);
    const campaignRaw = this.shareCampaign.replace('%movieUrl%', friendlyUrl);

    const message = encodeURIComponent(messageRaw);
    const subject = encodeURIComponent(subjectRaw);
    const campaign = encodeURIComponent(campaignRaw);

    const concatParams = (uri: string, params: string[]) => `${uri}${uri.includes('?') ? '&' : '?'}${params.join('&')}`;

    const currentUrl = window.location.href;

    const withUtm = (source: string) =>
      concatParams(currentUrl, [
        `utm_source=${encodeURIComponent(source)}`,
        'utm_medium=UserShared',
        `utm_campaign=${campaign}`
      ]);

    const source =
      sl.platform === 'whatsapp'
        ? 'Whatsapp'
        : sl.platform === 'facebook'
          ? 'Facebook'
          : sl.platform === 'twitter'
            ? 'Twitter'
            : sl.platform === 'bluesky'
              ? 'Bluesky'
              : sl.platform === 'mail'
                ? 'Email'
                : sl.platform === 'instagram'
                  ? 'Instagram'
                  : sl.platform;

    const trackedUrl = withUtm(source);

    switch (sl.platform) {
      case 'facebook': {
        const urlEncoded = encodeURIComponent(trackedUrl);
        return concatParams('https://facebook.com/sharer.php', [`u=${urlEncoded}`, `quote=${message}`]);
      }

      case 'twitter': {
        const urlEncoded = encodeURIComponent(trackedUrl);
        return concatParams('https://twitter.com/intent/tweet', [`text=${message}`, `url=${urlEncoded}`]);
      }

      case 'whatsapp': {
        const body = encodeURIComponent(`${messageRaw}\n\n${trackedUrl}`);
        return concatParams('https://api.whatsapp.com/send', [`text=${body}`]);
      }

      case 'bluesky': {
        const text = encodeURIComponent(`${messageRaw} ${trackedUrl}`);
        return concatParams('https://bsky.app/intent/compose', [`text=${text}`]);
      }

      case 'mail': {
        const body = encodeURIComponent(`${messageRaw}\n\n${trackedUrl}`);
        return `mailto:?subject=${subject}&body=${body}`;
      }

      case 'instagram': {
        // No web “share url intent” exists. Keep href safe.
        return 'https://www.instagram.com/';
      }

      default:
        return '';
    }
  }

  /**
   * @docsNotRequired
   * @param sl
   */
  onShareLinkClicked(sl: SocialLink, event?: MouseEvent) {
    // IMPORTANT: prevent the browser from following the href
    // (we will open the share ourselves to avoid double-open and to support Instagram)
    event?.preventDefault();

    const movie = this.movie();
    if (!this.isBrowser || !movie) return;

    const extraProps: Record<string, unknown> = {};
    extraProps['idMovie'] = movie.id;
    extraProps['movie'] = movie.title;
    extraProps['imdb'] = movie?.imdb;
    extraProps['tagsMovie'] = getMovieTags(movie);
    extraProps['genresMovie'] = getMovieGenres(movie);

    // Pure label (safe — no side effects)
    const label = this.onShareLink(sl);

    this.trackingService.trackEvent({
      action: 'ShareSocial',
      properties: {
        category: this.resolvedTrackingCategory,
        label,
        socialPlatform: sl.platform,
        ...extraProps
      }
    });

    // Instagram: try native share sheet, else open instagram.com
    if (sl.platform === 'instagram') {
      const movieTitle = movie.title || '';
      const friendlyUrl = movie.friendlyUrl || '';
      const messageRaw = this.shareMessage.replace('_movie_', movieTitle);
      const campaignRaw = this.shareCampaign.replace('%movieUrl%', friendlyUrl);
      const campaign = encodeURIComponent(campaignRaw);

      const concatParams = (uri: string, params: string[]) =>
        `${uri}${uri.includes('?') ? '&' : '?'}${params.join('&')}`;

      const trackedUrl = concatParams(window.location.href, [
        `utm_source=Instagram`,
        'utm_medium=UserShared',
        `utm_campaign=${campaign}`
      ]);

      shareOrFallbackToInstagram({ title: movieTitle, text: messageRaw, url: trackedUrl });

      return;
    }

    // Other platforms: open the URL we computed
    if (label) {
      window.open(label, '_blank', 'noopener,noreferrer');
    }
  }

  get isBrowser() {
    return isPlatformBrowser(this.platformId);
  }

  private getBaseSocialLinks(): SocialLink[] {
    const links: SocialLink[] = [
      {
        icon: faFacebook as IconLookup,
        iconProp: 'facebook',
        platform: 'facebook',
        to: '/'
      },
      {
        icon: faXTwitter as IconLookup,
        iconProp: 'x-twitter',
        platform: 'twitter',
        to: '/'
      },
      {
        icon: faWhatsapp as IconLookup,
        iconProp: 'whatsapp',
        platform: 'whatsapp',
        to: '/'
      },
      {
        icon: faEnvelope as IconLookup,
        iconProp: 'envelope',
        platform: 'mail',
        to: '/'
      },
      {
        icon: faInstagram as IconLookup,
        iconProp: 'instagram',
        platform: 'instagram',
        to: '/'
      }
    ];

    if (this.showBlueSky) {
      links.push({
        icon: faBluesky as IconLookup,
        iconProp: 'bluesky',
        platform: 'bluesky',
        to: '/'
      });
    }

    return links;
  }

  private buildSocialLinks(): SocialLink[] {
    const config = this.environmentService.tellFriendsComponent();
    const allLinks = this.getBaseSocialLinks();
    const defaultPlatforms: TellFriendsPlatform[] = ['facebook', 'twitter', 'whatsapp', 'mail'];
    const baseLinks = defaultPlatforms
      .map(platform => allLinks.find(link => link.platform === platform))
      .filter(Boolean) as SocialLink[];

    let links = baseLinks;
    if (config?.show?.length) {
      const showOrder = config.show.map(item => item.toLowerCase() as TellFriendsPlatform);
      links = showOrder
        .map(platform => allLinks.find(link => link.platform === platform))
        .filter(Boolean) as SocialLink[];
    }
    if (config?.hide?.length) {
      const hideSet = new Set(config.hide.map(item => item.toLowerCase() as TellFriendsPlatform));
      links = links.filter(link => !hideSet.has(link.platform));
    }

    if (config?.order?.length) {
      const orderMap = new Map(
        config.order.map((platform, index) => [platform.toLowerCase() as TellFriendsPlatform, index])
      );
      const originalIndex = new Map(links.map((link, index) => [link.platform, index]));
      links = [...links].sort((a, b) => {
        const aOrder = orderMap.get(a.platform);
        const bOrder = orderMap.get(b.platform);
        if (aOrder !== undefined && bOrder !== undefined) return aOrder - bOrder;
        if (aOrder !== undefined) return -1;
        if (bOrder !== undefined) return 1;
        return (originalIndex.get(a.platform) ?? 0) - (originalIndex.get(b.platform) ?? 0);
      });
    }

    return links;
  }
}

async function shareOrFallbackToInstagram(opts: { title?: string; text: string; url: string }) {
  const isMobile = typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  // Desktop: don't try to be clever — Instagram has no web share intent anyway.
  if (!isMobile) {
    window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
    return { kind: 'opened-instagram-web' as const };
  }

  // Mobile: best effort = native share sheet (closest to YouTube UX)
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      // Some browsers expose canShare; if present, use it to avoid known failures
      const canShare =
        typeof (navigator as any).canShare !== 'function' || (navigator as any).canShare({ url: opts.url });

      if (canShare) {
        await navigator.share({
          title: opts.title,
          text: opts.text,
          url: opts.url
        });
        return { kind: 'native-share' as const };
      }
    } catch {
      // user cancelled / share failed → fall through
    }
  }

  // Mobile fallback: open Instagram app if possible, otherwise web
  const ua = navigator.userAgent || '';
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);

  if (isIOS) {
    // best-effort open app
    window.location.href = 'instagram://app';
    // if app isn't installed / blocked, fallback to web after a short delay
    setTimeout(() => {
      window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
    }, 700);
    return { kind: 'attempt-open-app-ios' as const };
  }

  if (isAndroid) {
    // best-effort open app via intent
    window.location.href = 'intent://instagram.com/#Intent;scheme=https;package=com.instagram.android;end';
    setTimeout(() => {
      window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
    }, 700);
    return { kind: 'attempt-open-app-android' as const };
  }

  window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
  return { kind: 'opened-instagram-web' as const };
}
